/**
 * Composant d'affichage d'avatar avec fallback.
 * Affiche l'image de profil ou un avatar par défaut avec initiales.
 */
import React, { useState } from 'react';
import { User } from 'lucide-react';
import { getImageUrl } from '../../utils/imageUtils';

interface AvatarDisplayProps {
  avatarUrl?: string;
  name: string;
  size?: string;
  className?: string;
  showOnlineStatus?: boolean;
  isOnline?: boolean;
  forceColor?: string; // Force a specific color gradient
  userRole?: string; // For role-based color selection
}

const AvatarDisplay: React.FC<AvatarDisplayProps> = ({ 
  avatarUrl, 
  name, 
  size = "w-8 h-8",
  className = "",
  showOnlineStatus = false,
  isOnline = false,
  forceColor,
  userRole
}) => {
  const [imageError, setImageError] = useState(false);

  // Extraire les initiales du nom
  const getInitials = (fullName: string): string => {
    // Special case for "Support" - only show "S"
    if (fullName === 'Support') {
      return 'S';
    }
    
    return fullName
      .split(' ')
      .map(word => word.charAt(0))
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  // Générer une couleur basée sur le nom ou le rôle
  const getColorFromName = (name: string): string => {
    console.log('AvatarDisplay color debug:', {
      name,
      userRole,
      forceColor,
      willUseRoleColor: !!userRole
    });
    
    // Si une couleur est forcée, l'utiliser
    if (forceColor) return forceColor;
    
    // Couleurs spécifiques par rôle
    if (userRole === 'apporteur') {
      console.log('Using apporteur orange color');
      return 'from-orange-400 to-orange-600'; // Couleur distinctive pour apporteurs
    }
    if (userRole === 'admin' || userRole === 'super_admin') {
      console.log('Using admin blue color');
      return 'from-blue-400 to-blue-600'; // Bleu pour les admins
    }
    if (userRole === 'client') {
      console.log('Using client green color');
      return 'from-green-400 to-green-600'; // Vert pour les clients
    }
    
    const colors = [
      'from-purple-400 to-purple-600',
      'from-blue-400 to-blue-600',
      'from-green-400 to-green-600',
      'from-yellow-400 to-yellow-600',
      'from-red-400 to-red-600',
      'from-indigo-400 to-indigo-600',
      'from-pink-400 to-pink-600',
      'from-teal-400 to-teal-600',
    ];
    
    const hash = name.split('').reduce((acc, char) => {
      return char.charCodeAt(0) + ((acc << 5) - acc);
    }, 0);
    
    return colors[Math.abs(hash) % colors.length];
  };

  const avatarElement = avatarUrl && !imageError ? (
    <img
      src={getImageUrl(avatarUrl)}
      alt={name}
      className={`${size} rounded-full object-cover border-2 border-white shadow-sm ${className}`}
      onError={() => setImageError(true)}
    />
  ) : (
    // Fallback avec initiales ou icône
    (() => {
      const initials = getInitials(name);
      const gradientColor = getColorFromName(name);
      
      return (
        <div className={`${size} bg-gradient-to-br ${gradientColor} rounded-full flex items-center justify-center border-2 border-white shadow-sm ${className}`}>
          {initials ? (
            <span className="text-white font-medium text-xs">
              {initials}
            </span>
          ) : (
            <User className="h-4 w-4 text-white" />
          )}
        </div>
      );
    })()
  );

  // If no online status needed, return just the avatar
  if (!showOnlineStatus) {
    return avatarElement;
  }

  // Wrap with relative container and add online status indicator
  return (
    <div className="relative inline-block">
      {avatarElement}
      {showOnlineStatus && (
        <div className={`absolute -bottom-0.5 -right-0.5 w-4 h-4 border-2 border-white rounded-full ${
          isOnline ? 'bg-green-500' : 'bg-gray-400'
        }`}></div>
      )}
    </div>
  );
};

export default AvatarDisplay;
